import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import ChevronLeft from "../../components/Atomos/Icons/ChevronLeft";
import Button from "../../components/staff/Button";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type {
  IHeader,
  IHeaderCellProps,
} from "../../components/Organismos/Table/FilteredTable.types";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import { getOfficeById } from "../../Services/Office";
import {
  getAccountingOfficeDetail,
  officeDisplayName,
} from "../../utils/accountingOfficeDetail";
import {
  isCreditPositiveLedgerKind,
  loadOfficeBalanceLedger,
  officeBalanceIsDebt,
  type BalanceLedgerEntry,
} from "../../utils/accountingOfficeBalance";
import {
  formatMoney,
  MEMBER_BALANCE_CREDIT_LABEL,
  MEMBERSHIP_BALANCE_LABEL,
} from "./accountingModalUi";
import {
  ACCOUNTING_INSET_PANEL_CLASS,
  ACCOUNTING_PANEL_CLASS,
  FEE_TYPE_LABEL_CLASS,
  SECTION_TITLE_CLASS,
} from "./accountingFirmPanels";
import AdjustOfficeBalanceModal from "./AdjustOfficeBalanceModal";
import { isAccountingTab, type AccountingTab } from "./accountingTabs";

function formatLedgerAmount(amount: number): string {
  const abs = Math.abs(amount);
  const formatted = formatMoney(abs);
  if (amount > 0) {
    return `+${formatted}`;
  }
  if (amount < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

function ledgerTypePillClass(kind: BalanceLedgerEntry["kind"]): string {
  if (isCreditPositiveLedgerKind(kind)) {
    return "inline-flex rounded-full bg-[#E8F7EE] px-3 py-1 text-xs font-semibold text-[#1F7A3F]";
  }
  return "inline-flex rounded-full bg-[#FDECEC] px-3 py-1 text-xs font-semibold text-primary";
}

function ledgerAmountClass(kind: BalanceLedgerEntry["kind"]): string {
  if (isCreditPositiveLedgerKind(kind)) {
    return "font-semibold tabular-nums text-[#1F7A3F]";
  }
  return "font-semibold tabular-nums text-primary";
}

type BalanceLedgerRow = BalanceLedgerEntry & {
  ledgerDate: string;
  ledgerType: string;
  amountDisplay: string;
};

function formatSignedBalance(amount: number): string {
  const abs = Math.abs(amount);
  const formatted = formatMoney(abs);
  if (amount > 0) {
    return `+${formatted}`;
  }
  if (amount < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

function officeBalanceAmountClass(balance: number): string {
  const base = "mt-2 text-2xl font-semibold tabular-nums";
  if (balance > 0) {
    return `${base} text-primary`;
  }
  if (balance < 0) {
    return `${base} text-[#1F7A3F]`;
  }
  return `${base} text-gray-font`;
}

function officeBalanceHelperText(balance: number): string {
  if (balance > 0) {
    return "Positive balance: this firm owes WSG the amount shown above.";
  }
  if (balance < 0) {
    return "Negative balance: this firm has these funds available to apply toward future invoices.";
  }
  return "This firm has no outstanding membership balance.";
}

export default function AccountingOfficeBalanceLedger(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const backTab = useMemo((): AccountingTab => {
    const fromTab = (location.state as { fromTab?: unknown } | null)?.fromTab;
    return isAccountingTab(fromTab) ? fromTab : "accountingAll";
  }, [location.state]);

  const [officeName, setOfficeName] = useState("");
  const [officeBalance, setOfficeBalance] = useState(0);
  const [officeRecord, setOfficeRecord] =
    useState<Awaited<ReturnType<typeof getAccountingOfficeDetail>>>(null);
  const [entries, setEntries] = useState<BalanceLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [ledgerRefreshKey, setLedgerRefreshKey] = useState(0);
  const loadedOfficeIdRef = useRef<string | null>(null);

  const loadLedger = useCallback(async (officeId: string) => {
    setLoading(loadedOfficeIdRef.current !== officeId);
    setError(null);
    try {
      const office = await getAccountingOfficeDetail(officeId);
      if (!office) {
        setError("Office not found.");
        setOfficeRecord(null);
        setEntries([]);
        return;
      }

      const rawOffice = await getOfficeById(officeId);
      const freshBalance = Number(rawOffice?.balance ?? office.balance ?? 0);

      const ledgerEntries = await loadOfficeBalanceLedger(officeId);

      setOfficeRecord(office);
      setOfficeName(officeDisplayName(office));
      setOfficeBalance(freshBalance);
      setEntries(ledgerEntries);
      loadedOfficeIdRef.current = officeId;
    } catch {
      setError("Unable to load credit ledger.");
      setOfficeRecord(null);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setError("Invalid office id.");
      setLoading(false);
      return;
    }
    void loadLedger(id);
  }, [id, loadLedger, ledgerRefreshKey]);

  useEffect(() => {
    if (!id || !officeRecord) {
      return;
    }

    setPageMeta({
      title: "Balance Management",
      breadcrumbLabels: {
        "/accounting": "Accounting",
        [`/accounting/${id}`]: officeDisplayName(officeRecord),
        [`/accounting/${id}/balance`]: "Balance Management",
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [clearPageMeta, id, officeRecord, setPageMeta]);

  const formatData = useCallback((data: unknown[]): BalanceLedgerRow[] => {
    if (!Array.isArray(data)) {
      return [];
    }
    return (data as BalanceLedgerEntry[]).map((entry) => ({
      ...entry,
      ledgerDate: entry.dateLabel,
      ledgerType: entry.typeLabel,
      amountDisplay: formatLedgerAmount(entry.amount),
    }));
  }, []);

  const typeCell = useCallback(
    ({ row }: IHeaderCellProps) => (
      <span className={ledgerTypePillClass(row.kind)}>{row.ledgerType}</span>
    ),
    [],
  );

  const amountCell = useCallback(
    ({ row }: IHeaderCellProps) => (
      <span className={ledgerAmountClass(row.kind)}>{row.amountDisplay}</span>
    ),
    [],
  );

  const columnsDef = useMemo<IHeader[]>(
    () => [
      {
        id: "sortAt",
        header: "Date",
        width: 80,
        cell: ({ row }) => row.ledgerDate,
      },
      {
        id: "ledgerType",
        header: "Type",
        width: 90,
        enableSorting: false,
        cell: typeCell,
      },
      { id: "user", header: "User", width: 90 },
      { id: "reasonNotes", header: "Reason / Notes", width: 140 },
      {
        id: "amount",
        header: "Amount",
        width: 70,
        enableSorting: false,
        cell: amountCell,
      },
    ],
    [amountCell, typeCell],
  );

  const excelFilename = useMemo(() => {
    const safeName =
      officeName
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, " ")
        .trim() || "office";
    return `${safeName}-credit-ledger.xlsx`;
  }, [officeName]);

  const balanceIsDebt = officeBalanceIsDebt(officeBalance);
  const balanceLabel = balanceIsDebt
    ? MEMBERSHIP_BALANCE_LABEL
    : MEMBER_BALANCE_CREDIT_LABEL;
  const detailPath = id ? `/accounting/${id}` : "/accounting";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-font"></div>
    );
  }

  if (error || !officeRecord || !id) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <p className="text-gray-font">{error ?? "Office not found."}</p>
        <Link
          to="/accounting"
          state={{ activeTab: backTab }}
          className="text-[#0070CD] hover:underline"
        >
          Back to Accounting
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6 bg-white p-6">
      <Link
        to={detailPath}
        state={location.state}
        className="inline-flex items-center gap-2 text-sm font-medium text-[#0070CD] hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to {officeName}
      </Link>

      <div className={`${ACCOUNTING_PANEL_CLASS} gap-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className={SECTION_TITLE_CLASS}>Balance Management</h1>
            <p className={FEE_TYPE_LABEL_CLASS}>
              Manage membership balance and credit for {officeName}
            </p>
          </div>
          <div className={`${ACCOUNTING_INSET_PANEL_CLASS} min-w-[220px]`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-font">
              {balanceLabel}
            </div>
            <div className={officeBalanceAmountClass(officeBalance)}>
              {formatSignedBalance(officeBalance)}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-gray-font">
              {officeBalanceHelperText(officeBalance)}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          text="Adjust Balance"
          color="primary"
          variant="filled"
          size="fixed"
          onClick={() => setAdjustOpen(true)}
        />
      </div>

      <FilteredTableCore
        key={`balance-ledger-${id}-${ledgerRefreshKey}`}
        tableKey={`/accounting/${id}/balance`}
        containerclassName="!px-0"
        columnsDef={columnsDef}
        showRowNumber
        showTopHorizontalScrollBar
        endpoint="/billing"
        clientData={entries}
        filtersData={[]}
        formatData={formatData}
        defaultSorting={{ field: "sortAt", order: "desc" }}
        defaultPageSize={20}
        persistPageSize={false}
        persistFilters={false}
        persistSorting={false}
        showSearchInput
        searchPlaceholder="Search by type, user, or reason..."
        emptyStateMessage="No ledger entries found."
        excelFilename={excelFilename}
      />

      <AdjustOfficeBalanceModal
        office={officeRecord}
        isOpen={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSaved={() => {
          setLedgerRefreshKey((key) => key + 1);
        }}
      />
    </div>
  );
}
